import { ref, computed, onMounted, onUnmounted } from 'vue';
import { applyNodeChanges, applyEdgeChanges } from '@vue-flow/core';
import FlowsAPI from '../api/flows';

export function vueFlowTypeFor(nodeType) {
  if (!nodeType) return 'actionNode';
  if (nodeType === 'trigger_flow') return 'actionNode';
  if (nodeType.startsWith('trigger_')) return 'triggerNode';
  if (nodeType === 'condition') return 'conditionNode';
  if (nodeType === 'go_to') return 'goToNode';
  if (nodeType === 'wait') return 'waitNode';
  if (nodeType === 'split') return 'splitNode';
  if (nodeType === 'divide') return 'divideNode';
  if (nodeType === 'release') return 'waitNode';
  if (nodeType === 'update_card') return 'actionNode';
  if (nodeType === 'duplicate_card') return 'actionNode';
  return 'actionNode';
}

// Maps trigger node types to FlowTrigger trigger_type values
export const TRIGGER_NODE_TO_TYPE = {
  trigger_manual: 'manual',
  trigger_scheduled: 'scheduled',
  trigger_card_created: 'card_created',
  trigger_card_entered_column: 'card_entered_column',
  trigger_card_status_changed: 'card_status_changed',
  trigger_card_stalled_no_move: 'card_stalled_no_move',
  trigger_card_stalled_no_status: 'card_stalled_no_status',
  trigger_card_stalled_no_interaction: 'card_stalled_no_interaction',
  trigger_card_due_date: 'card_due_date',
  trigger_contact_created: 'contact_created',
  trigger_contact_tag_added: 'contact_tag_added',
  trigger_contact_tag_removed: 'contact_tag_removed',
  trigger_conversation_message_received: 'conversation_message_received',
  trigger_conversation_message_sent: 'conversation_message_sent',
  trigger_task_created: 'task_created',
  trigger_task_completed: 'task_completed',
  trigger_task_due_date: 'task_due_date',
  trigger_meeting_scheduled: 'meeting_scheduled',
  trigger_meeting_status_changed: 'meeting_status_changed',
  trigger_meeting_date: 'meeting_date',
  trigger_enrollment_created: 'enrollment_created',
  trigger_contact_replied: 'contact_replied',
};

// Consults TRIGGER_NODE_TO_TYPE as the single source of truth — trigger_flow has prefix 'trigger_'
// but is an executable action, not a visual trigger node.
export function isVisualTrigger(nodeType) {
  if (!nodeType) return false;
  return Object.hasOwn(TRIGGER_NODE_TO_TYPE, nodeType);
}

// entry_node_id is the target of the trigger node's single outgoing edge (the first action node).
// Backend rejects null entry_node_id even on draft save — only connected triggers are persisted.
export function extractDraftTriggers(nodes, edges) {
  return nodes
    .filter(n => isVisualTrigger(n.data?.nodeType))
    .map(n => {
      const outgoing = edges.filter(e => e.source === n.id);
      const entryNodeId = outgoing.length === 1 ? outgoing[0].target : null;
      const operationalConfig = n.data.config ?? {};
      return {
        trigger_type: TRIGGER_NODE_TO_TYPE[n.data.nodeType] ?? n.data.nodeType,
        entry_node_id: entryNodeId,
        trigger_config: {
          ...operationalConfig,
          canvasPos: { x: n.position?.x ?? 0, y: n.position?.y ?? 0 },
        },
      };
    });
}

const TYPE_TO_TRIGGER_NODE = Object.fromEntries(
  Object.entries(TRIGGER_NODE_TO_TYPE).map(([k, v]) => [v, k])
);

// Reconstructs canvas from backend definition + draft_triggers.
// Trigger nodes are UI-only and NOT stored in draft_definition — they are reconstructed from draftTriggers.
export function definitionToCanvas(definition, draftTriggers = []) {
  const execNodes =
    definition && Array.isArray(definition.nodes) ? definition.nodes : [];
  const execEdges =
    definition && Array.isArray(definition.edges) ? definition.edges : [];

  const nodes = execNodes.map(n => ({
    id: n.id,
    type: vueFlowTypeFor(n.type),
    position: n.position ?? { x: 0, y: 0 },
    data: {
      nodeType: n.type,
      config: n.config ?? {},
      label: n.label ?? n.type,
    },
  }));

  const edges = execEdges.map(e => ({
    id: e.id,
    source: e.from,
    target: e.to,
    sourceHandle: e.source_handle ?? null,
    type: 'custom',
  }));

  // Reconstruct trigger nodes and their outgoing edges from draftTriggers
  (draftTriggers ?? []).forEach((t, i) => {
    const nodeType =
      TYPE_TO_TRIGGER_NODE[t.trigger_type] ?? `trigger_${t.trigger_type}`;
    const nodeId = `trigger_visual_${t.id ?? i}`;
    const visual = t.trigger_config?.canvasPos;
    const position = visual
      ? { x: visual.x, y: visual.y }
      : { x: 0, y: i * 120 };
    // Strip canvasPos from the config exposed to the node — it's canvas metadata, not operational data
    // eslint-disable-next-line no-unused-vars
    const { canvasPos, ...operationalConfig } = t.trigger_config ?? {};
    nodes.push({
      id: nodeId,
      type: 'triggerNode',
      position,
      data: {
        nodeType,
        config: operationalConfig,
        label: nodeType,
      },
    });
    if (t.entry_node_id) {
      edges.push({
        id: `trigger_edge_${nodeId}`,
        source: nodeId,
        target: t.entry_node_id,
        sourceHandle: null,
        type: 'custom',
      });
    }
  });

  return { nodes, edges };
}

// Trigger nodes are UI-only. The executable definition must contain only action/control nodes.
export function canvasToDefinition(nodes, edges) {
  const triggerNodeIds = new Set(
    nodes.filter(n => isVisualTrigger(n.data?.nodeType)).map(n => n.id)
  );
  const executableNodes = nodes.filter(n => !triggerNodeIds.has(n.id));
  const executableEdges = edges.filter(e => !triggerNodeIds.has(e.source));

  return {
    nodes: executableNodes.map(n => ({
      id: n.id,
      type: n.data.nodeType,
      config: n.data.config ?? {},
      position: n.position,
      label: n.data.label,
    })),
    edges: executableEdges.map(e => ({
      id: e.id,
      from: e.source,
      to: e.target,
      source_handle: e.sourceHandle ?? null,
    })),
  };
}

const VALIDATION_PATTERNS = [
  {
    regex: /^node '(.+)' is unreachable from any entry point$/,
    key: 'FLOWS.VALIDATION.UNREACHABLE',
    capturesNodeId: true,
  },
  {
    regex: /^condition node '(.+)' must have exactly one 'yes' branch$/,
    key: 'FLOWS.VALIDATION.CONDITION_MISSING_YES',
    capturesNodeId: true,
  },
  {
    regex: /^condition node '(.+)' must have exactly one 'no' branch$/,
    key: 'FLOWS.VALIDATION.CONDITION_MISSING_NO',
    capturesNodeId: true,
  },
  {
    regex:
      /at least one trigger with entry_node_id is required|trigger is missing entry_node_id/,
    key: 'FLOWS.VALIDATION.TRIGGER_DISCONNECTED',
    capturesNodeId: false,
  },
  {
    regex: /^unknown node type '.+' for node '(.+)'$/,
    key: 'FLOWS.VALIDATION.UNKNOWN_NODE_TYPE',
    capturesNodeId: true,
  },
];

export function parseValidationErrors(rawErrors, nodes, t) {
  const individual = rawErrors.flatMap(raw =>
    raw.includes('; ') ? raw.split('; ') : [raw]
  );
  return individual.map(raw => {
    const matched = VALIDATION_PATTERNS.reduce((found, pattern) => {
      if (found) return found;
      const match = raw.match(pattern.regex);
      if (!match) return null;
      return { pattern, match };
    }, null);

    if (!matched)
      return { message: t('FLOWS.VALIDATION.UNKNOWN', { raw }), nodeId: null };

    const { pattern, match } = matched;
    const nodeId = pattern.capturesNodeId ? match[1] : null;
    const node = nodeId ? nodes.find(n => n.id === nodeId) : null;
    const label = node ? (node.data.label ?? node.data.nodeType) : nodeId;
    return { message: t(pattern.key, { label, raw }), nodeId };
  });
}

function makeDebounce(fn, delay) {
  let timer = null;
  function debounced(...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  }
  debounced.cancel = () => clearTimeout(timer);
  return debounced;
}

export function useFlowEditor(flowIdRef) {
  const flow = ref(null);
  const nodes = ref([]);
  const edges = ref([]);
  const etag = ref(null);
  const isDirty = ref(false);
  const isSaving = ref(false);
  const saveError = ref(null);
  const conflictDetected = ref(false);
  const validationErrors = ref({});
  const selectedNodeId = ref(null);
  const isLoading = ref(false);
  const loadError = ref(null);

  // Sequence counter to drop stale save responses
  let saveSeq = 0;
  // Mutex: if a save is in-flight, set this flag instead of starting another PATCH with same ETag
  let pendingDirty = false;
  // Holds the Promise of the current in-flight save so flushSave can await it directly
  let activeSavePromise = null;
  // Set to true while flushSave is running — prevents the finally block from re-scheduling debounce
  let isFlushing = false;
  // Declared before save() so the no-use-before-define rule is satisfied.
  // Assigned after save() and saveQuietly() are defined below.
  let debouncedSave;

  const definition = computed(() =>
    canvasToDefinition(nodes.value, edges.value)
  );

  async function loadFlow() {
    const id = typeof flowIdRef === 'function' ? flowIdRef() : flowIdRef.value;
    if (!id) return;
    isLoading.value = true;
    loadError.value = null;
    try {
      const response = await FlowsAPI.show(id);
      const loaded = response.data.data;
      flow.value = loaded;
      etag.value = loaded.lock_version;
      const draftDef = loaded.draft_version?.definition ?? {
        nodes: [],
        edges: [],
      };
      const draftTriggers = loaded.draft_triggers ?? [];
      const canvas = definitionToCanvas(draftDef, draftTriggers);
      nodes.value = canvas.nodes;
      edges.value = canvas.edges;
      isDirty.value = false;
      saveError.value = null;
    } catch (e) {
      loadError.value = e;
    } finally {
      isLoading.value = false;
    }
  }

  // save() returns true on success, throws on hard errors, returns false on conflict/stale
  async function save() {
    const id = typeof flowIdRef === 'function' ? flowIdRef() : flowIdRef.value;
    if (!id || conflictDetected.value) return false;
    if (etag.value === null || etag.value === undefined) return false;

    // Mutex: never send two concurrent PATCHes with the same ETag.
    // If another save is in-flight, mark pendingDirty and let that save's finally block re-schedule.
    if (isSaving.value) {
      pendingDirty = true;
      return false;
    }

    saveSeq += 1;
    const seq = saveSeq;
    isSaving.value = true;
    pendingDirty = false;
    saveError.value = null;

    const draftTriggers = extractDraftTriggers(nodes.value, edges.value);

    const request = FlowsAPI.saveDefinition(
      id,
      definition.value,
      flow.value?.draft_version?.execution_settings ?? {},
      etag.value,
      draftTriggers
    );
    activeSavePromise = request;

    try {
      const response = await request;

      // Discard stale responses — another save was started after this one
      if (seq !== saveSeq) return false;

      const updated = response.data.data;
      flow.value = updated;
      etag.value = updated.lock_version;
      // Only clear dirty if no change arrived while we were in-flight.
      // If pendingDirty is set, the canvas changed during this save;
      // isDirty must remain true until that pending save also completes.
      isDirty.value = pendingDirty;
      return true;
    } catch (e) {
      if (seq !== saveSeq) return false;

      if (e?.response?.status === 409) {
        conflictDetected.value = true;
        return false;
      }
      // Network/server error — preserve dirty so user can retry
      saveError.value = e;
      // Rethrow so callers (publish / flushSave) know the save failed
      throw e;
    } finally {
      if (seq === saveSeq) {
        isSaving.value = false;
        activeSavePromise = null;
        // Only re-schedule when not in the middle of a flush — flushSave will handle the final save itself
        if (pendingDirty && !conflictDetected.value && !isFlushing) {
          pendingDirty = false;
          debouncedSave();
        }
      }
    }
  }

  // Wrap save in a fire-and-forget so markDirty never produces unhandled rejections.
  // Errors are surfaced via saveError ref; publish callers await save() directly.
  function saveQuietly() {
    save().catch(() => {});
  }

  debouncedSave = makeDebounce(saveQuietly, 2000);

  function markDirty() {
    isDirty.value = true;
    if (!conflictDetected.value) {
      debouncedSave();
    }
  }

  function onNodesChange(changes) {
    nodes.value = applyNodeChanges(changes, nodes.value);
    changes.forEach(change => {
      if (change.type === 'position' && change.dragging === false) {
        markDirty();
      }
      if (change.type === 'remove') {
        markDirty();
      }
    });
  }

  function onEdgesChange(changes) {
    edges.value = applyEdgeChanges(changes, edges.value);
    if (changes.length > 0) markDirty();
  }

  function onConnect() {
    markDirty();
  }

  function updateNodeConfig(nodeId, config) {
    nodes.value = nodes.value.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, config } } : n
    );
    markDirty();
  }

  function updateNodeLabel(nodeId, label) {
    nodes.value = nodes.value.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, label } } : n
    );
    markDirty();
  }

  function deleteNode(nodeId) {
    nodes.value = nodes.value.filter(n => n.id !== nodeId);
    edges.value = edges.value.filter(
      e => e.source !== nodeId && e.target !== nodeId
    );
    if (selectedNodeId.value === nodeId) selectedNodeId.value = null;
    markDirty();
  }

  function addNode(nodeType, position) {
    const id = `node_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    nodes.value = [
      ...nodes.value,
      {
        id,
        type: vueFlowTypeFor(nodeType),
        position: position ?? { x: 200, y: 200 },
        data: { nodeType, config: {}, label: nodeType },
      },
    ];
    markDirty();
    return id;
  }

  async function reload() {
    conflictDetected.value = false;
    isDirty.value = false;
    saveError.value = null;
    debouncedSave.cancel();
    await loadFlow();
  }

  // Drains the full autosave pipeline before publish.
  // Returns true when the canvas is fully persisted, false if aborted (conflict/error).
  // Steps:
  //   1. Set isFlushing so the save finally block does not re-schedule the debounce.
  //   2. Cancel any pending debounce timer.
  //   3. Await the in-flight PATCH (activeSavePromise) with a 15 s timeout.
  //   4. Cancel the debounce again — the finally may have set a new timer before isFlushing was set.
  //   5. If changes are still pending (pendingDirty or isDirty), run one explicit save.
  //   6. Clear isFlushing.
  async function flushSave() {
    isFlushing = true;
    debouncedSave.cancel();

    try {
      // Await the in-flight PATCH directly (no polling) with a 15 s safety timeout
      if (activeSavePromise) {
        const timeout = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('flushSave timeout')), 15000);
        });
        await Promise.race([activeSavePromise, timeout]).catch(() => {});
      }

      // Cancel any debounce that the save's finally scheduled before isFlushing was seen
      debouncedSave.cancel();

      if (conflictDetected.value || saveError.value) return false;

      // Run one final explicit save if changes are pending
      if (pendingDirty || isDirty.value) {
        pendingDirty = false;
        const saved = await save();
        if (!saved) return false;
      }

      if (!isDirty.value && !saveError.value && !conflictDetected.value) {
        return { ok: true, etag: etag.value };
      }
      return { ok: false, etag: etag.value };
    } finally {
      isFlushing = false;
    }
  }

  function beforeUnloadHandler(e) {
    if (isDirty.value) {
      e.preventDefault();
      e.returnValue = '';
    }
  }

  onMounted(() => {
    loadFlow();
    window.addEventListener('beforeunload', beforeUnloadHandler);
  });

  onUnmounted(() => {
    debouncedSave.cancel();
    window.removeEventListener('beforeunload', beforeUnloadHandler);
  });

  return {
    flow,
    nodes,
    edges,
    etag,
    isDirty,
    isSaving,
    saveError,
    conflictDetected,
    validationErrors,
    selectedNodeId,
    isLoading,
    loadError,
    definition,
    save,
    flushSave,
    reload,
    onNodesChange,
    onEdgesChange,
    onConnect,
    updateNodeConfig,
    updateNodeLabel,
    deleteNode,
    addNode,
    markDirty,
  };
}
